import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { createContext, useState, useEffect } from "react";
import Script from "next/script";
import { useRouter } from "next/router";
import { SplitIoProvider } from "@miro-site/splitio";
import { eventHub, getAnalytics } from "@miro-site/analytics";
export var ServicesContext = /*#__PURE__*/ createContext({
    analytics: undefined,
    eventHub: undefined
});
var userActions = [
    "mousemove",
    "keydown",
    "click",
    "touchstart"
];
export var ServicesProvider = function(param) {
    var children = param.children, _withAnalytics = param.withAnalytics, withAnalytics = _withAnalytics === void 0 ? true : _withAnalytics, _withEventHub = param.withEventHub, withEventHub = _withEventHub === void 0 ? true : _withEventHub, _withSplitIo = param.withSplitIo, withSplitIo = _withSplitIo === void 0 ? true : _withSplitIo;
    var router = useRouter();
    var ref = useState(false), oneTrustIsLoaded = ref[0], setOneTrustIsLoaded = ref[1];
    var ref1 = useState(false), loadOneTrust = ref1[0], setLoadOneTrust = ref1[1];
    var ref2 = useState(false), loadAnalytics = ref2[0], setLoadAnalytics = ref2[1];
    // Directly load Split IO to avoid screen flickering with experiments above the fold
    var loadSplitIo = withSplitIo && !!process.env.NEXT_PUBLIC_SPLITIO_CLIENT_KEY;
    // GTM
    var analytics = getAnalytics();
    // Add listener to trigger "page view" at route change
    useEffect(function() {
        if (!withEventHub || !eventHub) {
            return;
        }
        // Trigger page view for initial load
        eventHub.page();
        // Setup callback for route changes
        router.events.on("routeChangeComplete", eventHub.page);
        // Remove event tracking when provider is unmounted
        return function() {
            router.events.off("routeChangeComplete", eventHub.page);
        };
    }, [
        withEventHub,
        eventHub
    ]);
    // Add listener to load `OneTrust`
    useEffect(function() {
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = userActions[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var action = _step.value;
                window.addEventListener(action, handleUserAction);
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
        // Return clean-up function
        return removeInteractionListeners;
    }, []);
    // When One Trust is loaded, enable analytics to be loaded
    useEffect(function() {
        if (!oneTrustIsLoaded) {
            return;
        }
        setLoadAnalytics(withAnalytics);
    }, [
        oneTrustIsLoaded
    ]);
    // Initialize analytics providers
    useEffect(function() {
        if (loadAnalytics && analytics) {
            analytics.initialize();
        }
    }, [
        loadAnalytics,
        analytics
    ]);
    // Handler initial user action
    var handleUserAction = function(_e) {
        // Enable One Trust script to be loaded
        setLoadOneTrust(true);
        // Remove listeners to avoid repeating this function
        removeInteractionListeners();
    };
    // Clean up function to remove the user action listeners
    var removeInteractionListeners = function() {
        var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
        try {
            for(var _iterator = userActions[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
                var action = _step.value;
                window.removeEventListener(action, handleUserAction);
            }
        } catch (err) {
            _didIteratorError = true;
            _iteratorError = err;
        } finally{
            try {
                if (!_iteratorNormalCompletion && _iterator.return != null) {
                    _iterator.return();
                }
            } finally{
                if (_didIteratorError) {
                    throw _iteratorError;
                }
            }
        }
    };
    var handleOneTrustOnLoad = function() {
        // Update flag to load other scripts only when OneTrust is initialized
        var checkOneTrustInit = setInterval(function() {
            if (window.OneTrust) {
                setOneTrustIsLoaded(true);
                window.dispatchEvent(new CustomEvent("oneTrustLoaded"));
                // Clear interval to stop looping
                clearInterval(checkOneTrustInit);
            }
        }, 300);
    };
    var content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Script, {
                strategy: "beforeInteractive",
                // Removing next/script from first render negatively impacts performance.
                // We are explicitly using CDN host to load optanonWrapper using next/script.
                src: "https://static-website.miro.com/miro-site-lp-build-assets/scripts/optanonWrapper.js"
            }),
            loadOneTrust && /*#__PURE__*/ _jsx(Script, {
                src: "https://cdn.cookielaw.org/consent/4133a0ef-6999-461d-909d-24f2b05bbc15/otSDKStub.js",
                "data-document-language": "true",
                charSet: "UTF-8",
                "data-domain-script": "4133a0ef-6999-461d-909d-24f2b05bbc15",
                onLoad: handleOneTrustOnLoad
            }),
            children
        ]
    });
    if (loadSplitIo) {
        content = /*#__PURE__*/ _jsx(SplitIoProvider, {
            children: content
        });
    }
    return /*#__PURE__*/ _jsx(ServicesContext.Provider, {
        value: {
            analytics: analytics,
            eventHub: eventHub
        },
        children: content
    });
};
